import { Check, Circle, Minus } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { type CreditDetail, formatCreditInstant } from '../models/credit-list'
import { buildCreditStageTimeline } from '../operations/credit-detail-view'

const STAGE_VISUALS = {
  completed: {
    icon: Check,
    dot: 'bg-primary text-primary-foreground',
    line: 'bg-primary/35',
    label: 'Completada'
  },
  active: {
    icon: Circle,
    dot: 'bg-[#008A76] text-white ring-4 ring-[#008A76]/15',
    line: 'bg-border',
    label: 'Actual'
  },
  pending: {
    icon: Circle,
    dot: 'bg-muted text-muted-foreground',
    line: 'bg-border',
    label: 'Pendiente'
  },
  skipped: {
    icon: Minus,
    dot: 'bg-muted/60 text-muted-foreground',
    line: 'bg-border',
    label: 'Salteada'
  }
} as const

export function CreditStageTimeline({ stages }: Pick<CreditDetail, 'stages'>) {
  const timeline = buildCreditStageTimeline(stages)

  return (
    <ol className="space-y-0" aria-label="Recorrido del crédito">
      {timeline.map((stage, index) => {
        const visual = STAGE_VISUALS[stage.status]
        const Icon = visual.icon
        const isLast = index === timeline.length - 1
        return (
          <li key={stage.key} className="relative flex gap-3 pb-4 last:pb-0">
            {!isLast ? (
              <span
                aria-hidden="true"
                className={cn('absolute left-[11px] top-6 h-[calc(100%-1rem)] w-px', visual.line)}
              />
            ) : null}
            <span
              className={cn(
                'relative z-[1] flex size-6 shrink-0 items-center justify-center rounded-full',
                visual.dot
              )}
            >
              <Icon aria-hidden="true" className="size-3" />
            </span>
            <div className="min-w-0 flex-1 pt-0.5">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-medium leading-5">{stage.name}</p>
                <span className="shrink-0 text-[11px] text-muted-foreground">{visual.label}</span>
              </div>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {stage.status === 'pending'
                  ? 'Todavía no inició'
                  : stage.status === 'skipped'
                    ? 'No aplica a esta modalidad'
                    : `${stage.durationDays ?? 0} días · ${formatCreditInstant(stage.enteredAt)}`}
              </p>
              {stage.completedBy ? (
                <p className="mt-0.5 truncate text-xs text-muted-foreground">
                  Cerrada por {stage.completedBy.fullname}
                </p>
              ) : null}
            </div>
          </li>
        )
      })}
    </ol>
  )
}
